import React, { createContext, useState, useEffect } from "react";
import { login as loginV2, changePassword as changePasswordV2 } from "../../utils/services/control/authService";
import { logout as logoutV2 } from "../../utils/services/control/sessionService";
import { getEmployeeInfo, getEmployeeLocations, getRoleModules } from "../../utils/services/control/masterDataService";
import { isTokenExpired } from "../../utils/services/Auth/isTokenExpired";
import secureStorage from "../../utils/security/secureStorage";
import resetPasswordService from "../../utils/services/Management/resetPassword";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load stored user on app start
  // Token in localStorage for API compatibility (77+ services use it)
  // User data in secureStorage (encrypted) for HIPAA compliance
  useEffect(() => {
    const storedUser = secureStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (storedUser && token && !isTokenExpired(token)) {
      setUser(storedUser);
    } else {
      secureStorage.removeItem("user");
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      setUser(null);
    }

    setLoading(false);
  }, []);

  /**
   * Persiste userData en localStorage y secureStorage.
   * Centraliza la lógica de almacenamiento para evitar duplicación.
   */
  const persistUser = (userData) => {
    setUser(userData);
    // Token in localStorage for API service compatibility (77+ services)
    localStorage.setItem("token", userData.token);
    // User data encrypted in secureStorage (HIPAA - PHI protection)
    secureStorage.setItem("user", userData);
    // User data in localStorage for legacy compatibility
    localStorage.setItem("user", JSON.stringify(userData));
  };

  /**
   * Sign in using AuthV2 endpoints + post-login MasterData calls.
   * Flujo según 10_LOGIN_SESSION_GUIDE.md:
   *   1. Login (AuthV2) → token + employeeId + sessionId
   *   2. getEmployeeInfo → datos del empleado + roles
   *   3. getEmployeeLocations → locations asignadas
   *   4. Si 1 rol → getRoleModules automáticamente
   *      Si 2+ roles → retornar con requireRoleSelection=true para que LoginPage muestre selector
   *
   * Returns the full userData so the LoginPage can check flags and handle role selection.
   * On error, throws to let the caller handle UI feedback.
   */
  const signIn = async (email, password) => {
    const data = await loginV2({ email, password });

    // Si login fue exitoso (result=0), continuar con flujo post-login
    if (data && data.result === 0 && data.token) {
      // Guardar token inmediatamente (necesario para change-password y post-login calls)
      localStorage.setItem("token", data.token);

      // Si debe cambiar contraseña → NO hacer post-login calls.
      // Guardar solo lo mínimo para que ChangePasswordPage funcione (necesita JWT).
      // Después de cambiar contraseña, se hace signOut + login fresco con flags en false.
      if (data.forcePasswordChange || data.passwordExpired) {
        const minimalUserData = {
          token: data.token,
          employeeId: data.employeeId,
          sessionId: data.sessionId,
          forcePasswordChange: data.forcePasswordChange,
          passwordExpired: data.passwordExpired,
        };

        persistUser(minimalUserData);
        return { ...data, userData: minimalUserData };
      }

      // ── Flujo normal post-login (sin cambio de contraseña obligatorio) ──

      // Paso 2: Obtener info del empleado + roles
      const employeeInfoRows = await getEmployeeInfo(data.employeeId);
      const firstRow = employeeInfoRows[0] || {};

      // Extraer roles únicos (una fila por rol)
      const roles = employeeInfoRows
        .filter((row) => row.roleId != null)
        .map((row) => ({ id: row.roleId, name: row.roleName, key: row.roleKey }));

      // Paso 3: Obtener locations asignadas
      const locationsData = await getEmployeeLocations(data.employeeId);
      const locations = locationsData.map((loc) => ({
        id: loc.locationId,
        key: loc.centerKey,
        name: loc.medicalCenter,
        isPrimary: loc.isPrimary,
        address: loc.addressOffice,
      }));
      const primaryLocation = locations.find((loc) => loc.isPrimary) || locations[0] || null;

      // Construir userData base con datos REALES del backend
      const userData = {
        token: data.token,
        employeeId: data.employeeId,
        sessionId: data.sessionId,
        forcePasswordChange: data.forcePasswordChange,
        passwordExpired: data.passwordExpired,
        firstName: firstRow.firstName,
        lastName: firstRow.lastName,
        fullName: `${firstRow.firstName} ${firstRow.lastName}`,
        email: firstRow.mail,
        businessEmail: firstRow.businessEmail,
        jobPositionId: firstRow.jobPositionId,
        jobPositionName: firstRow.jobPositionName,
        photo: firstRow.photo,
        roles,
        activeRole: null,
        locations,
        primaryLocation,
        modules: [],
        // Legacy compatibility fields
        rol: roles[0]?.id?.toString() || null,
        location: primaryLocation?.key || null,
        employee: data.employeeId,
      };

      // Paso 4: Evaluar cantidad de roles
      if (roles.length === 1) {
        // Solo 1 rol → seleccionar automáticamente y cargar módulos
        const modulesData = await getRoleModules(data.employeeId, roles[0].id);
        // getRoleModules retorna array de módulos si éxito, o {result, message} si error
        const modules = Array.isArray(modulesData)
          ? modulesData.map((m) => ({
              id: m.moduleId,
              name: m.moduleName,
              key: m.moduleKey,
              canCreate: m.canCreate,
              canRead: m.canRead,
              canUpdate: m.canUpdate,
              canDelete: m.canDelete,
              hasAccess: m.hasAccess,
            }))
          : [];

        userData.activeRole = roles[0];
        userData.modules = modules;
        userData.rol = roles[0].id.toString();
      } else if (roles.length > 1) {
        // 2+ roles → marcar para selección en LoginPage
        userData.requireRoleSelection = true;
      }

      persistUser(userData);
      return { ...data, userData };
    }

    // Si result != 0, el backend retornó un error de login (401)
    // Lanzar para que LoginPage maneje los códigos de error
    const error = new Error(data?.message || "Login failed");
    error.response = { data };
    throw error;
  };

  /**
   * Seleccionar/cambiar rol activo y cargar módulos correspondientes.
   * Se usa después del login (si el usuario tiene 2+ roles) o durante la sesión
   * para cambiar de rol desde un dropdown.
   *
   * @param {number} roleId - ID del rol a activar
   */
  const switchRole = async (roleId) => {
    if (!user) throw new Error("No user session");

    const modulesData = await getRoleModules(user.employeeId, roleId);

    // Si getRoleModules retornó error (objeto con result > 0)
    if (!Array.isArray(modulesData) && modulesData?.result > 0) {
      const error = new Error(modulesData.message || "Failed to load role modules");
      error.response = { data: modulesData };
      throw error;
    }

    const modules = Array.isArray(modulesData)
      ? modulesData.map((m) => ({
          id: m.moduleId,
          name: m.moduleName,
          key: m.moduleKey,
          canCreate: m.canCreate,
          canRead: m.canRead,
          canUpdate: m.canUpdate,
          canDelete: m.canDelete,
          hasAccess: m.hasAccess,
        }))
      : [];

    const selectedRole = user.roles.find((r) => r.id === roleId) || null;

    const updatedUser = {
      ...user,
      activeRole: selectedRole,
      modules,
      requireRoleSelection: false,
      // Legacy compatibility
      rol: roleId.toString(),
    };

    persistUser(updatedUser);
    return updatedUser;
  };

  /**
   * Change password using AuthV2 endpoint.
   * Returns the response data so the ChangePasswordPage
   * can check result codes and display appropriate messages.
   */
  const changePassword = async (currentPassword, newPassword) => {
    const data = await changePasswordV2({ currentPassword, newPassword });
    return data;
  };

  /**
   * Reset password (admin action) - uses legacy endpoint.
   */
  const resetPassword = async (idEmployee, newPassword) => {
    try {
      const data = await resetPasswordService(idEmployee, newPassword);
      return data;
    } catch (error) {
      console.error("Error al resetear la contraseña");
      throw error;
    }
  };

  /**
   * Sign out - calls the new logout endpoint to invalidate session in DB,
   * then clears all local storage.
   */
  const signOut = async () => {
    // Intentar cerrar sesión en el backend (no bloquear si falla)
    try {
      const token = localStorage.getItem("token");
      if (token && !isTokenExpired(token)) {
        await logoutV2();
      }
    } catch (err) {
      // Ignorar errores de red en logout - limpiar de todos modos
    }

    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    secureStorage.clear();
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, changePassword, resetPassword, switchRole }}>
      {children}
    </AuthContext.Provider>
  );
};
